import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { MessageCircle, Mail, Info, Loader2, Send } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { euro } from "@/lib/format";
import QuotePDF from "@/components/QuotePDF";
import DdtPDF from "@/components/ddt/DdtPDF";
import { elementPdfFile, fileToBase64 } from "@/lib/pdf";
import api from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const DOCS = {
  preventivo: { the: "il preventivo", T: "Preventivo", pdf: "PREVENTIVO" },
  fattura: { the: "la fattura", T: "Fattura", pdf: "FATTURA" },
  ddt: { the: "il documento di trasporto (DDT)", T: "DDT", pdf: "DDT" },
};

const sanitizePhone = (raw) => {
  let d = (raw || "").replace(/[^\d]/g, "");
  if (!d) return "";
  if (d.startsWith("00")) d = d.slice(2);
  if (!d.startsWith("39") && d.length <= 11) d = "39" + d;
  return d;
};

const download = (file) => {
  const url = URL.createObjectURL(file);
  const a = Object.assign(document.createElement("a"), { href: url, download: file.name });
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 3000);
};

export default function SendDialog({ open, onClose, doc, contact, settings, docType = "preventivo", initialChannel = "whatsapp" }) {
  const [channel, setChannel] = useState(initialChannel);
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [mailOk, setMailOk] = useState(false);
  const [waOk, setWaOk] = useState(false);
  const { user } = useAuth();
  const D = DOCS[docType] || DOCS.preventivo;
  const fname = `${D.T}-${String(doc?.numero || "").replace(/\//g, "-")}.pdf`;
  const subject = `${D.T} N. ${doc?.numero || ""} - ${settings?.ragione_sociale || ""}`;

  const defaultMessage = useMemo(() => {
    if (!doc) return "";
    const azienda = settings?.ragione_sociale || "";
    const isDdt = docType === "ddt";
    const righe = [
      `Gentile ${doc.cliente_nome || doc.destinatario_nome || "Cliente"},`,
      `in allegato ${D.the} N. ${doc.numero} di ${azienda}.`,
      doc.oggetto ? `Oggetto: ${doc.oggetto}` : "",
      isDdt && doc.causale ? `Causale: ${doc.causale}` : "",
      isDdt ? "" : `Importo totale: ${euro(doc.totale)} (IVA inclusa).`,
      isDdt ? "" : `Modalità di pagamento: ${doc.termini_pagamento || settings?.termini_default || ""}`,
      "",
      `Restiamo a disposizione.`,
      `${azienda}${settings?.telefono ? " · Tel. " + settings.telefono : ""}`,
    ];
    return righe.filter((r) => r !== "").join("\n");
  }, [doc, settings, docType, D]);

  useEffect(() => {
    if (!open) return;
    setChannel(initialChannel);
    setPhone(sanitizePhone(contact?.telefono));
    setEmail(contact?.email || "");
    setMessage(defaultMessage);
    if (user?.role === "admin") {
      api.get("/mail/account").then((r) => setMailOk(!!r.data.configured)).catch(() => setMailOk(false));
      api.get("/whatsapp/config").then((r) => setWaOk(!!r.data.account?.connected)).catch(() => setWaOk(false));
    }
  }, [open, contact, defaultMessage, user?.role, initialChannel]);

  if (!doc) return null;

  const run = async (fn) => {
    setBusy(true);
    try { await fn(await elementPdfFile("send-print-area", fname)); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Invio non riuscito, riprova"); }
    finally { setBusy(false); }
  };

  // WhatsApp: sul telefono la condivisione allega il PDF; sul computer lo scarica e apre WhatsApp Web.
  const sendWhatsapp = () => run(async (file) => {
    if (waOk) {
      const num = sanitizePhone(phone);
      if (!num) { toast.error("Inserisci un numero di telefono valido"); return; }
      const fd = new FormData();
      fd.append("to", num); fd.append("caption", message); fd.append("file", file);
      await api.post("/whatsapp/send-file", fd);
      toast.success("PDF inviato su WhatsApp");
      onClose();
      return;
    }
    if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({ files: [file], text: message, title: fname.replace(".pdf", "") });
        return;
      } catch (err) {
        if (err?.name === "AbortError") return;
      }
    }
    if (file) download(file);
    const num = sanitizePhone(phone);
    window.open(num ? `https://wa.me/${num}?text=${encodeURIComponent(message)}` : `https://wa.me/?text=${encodeURIComponent(message)}`, "_blank");
    toast.message("PDF scaricato: trascinalo nella chat di WhatsApp Web.");
  });

  const sendEmail = () => {
    if (!email) { toast.error("Inserisci un indirizzo email"); return; }
    run(async (file) => {
      if (mailOk) {
        const attachments = [{ filename: fname, mime: "application/pdf", content_b64: await fileToBase64(file) }];
        await api.post("/mail/send", { to: email, subject, text: message, attachments });
        toast.success(`Email inviata a ${email} con il PDF allegato`);
      } else {
        const fd = new FormData();
        fd.append("to", email); fd.append("subject", subject); fd.append("text", message); fd.append("file", file);
        await api.post("/documents/send-link", fd);
        toast.success(`Email inviata a ${email} con il PDF da scaricare`);
      }
      onClose();
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[92dvh] overflow-y-auto" data-testid="send-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Invia {docType === "ddt" ? "DDT" : D.T.toLowerCase()} N. {doc.numero}</DialogTitle>
          <DialogDescription>{mailOk ? "Scegli WhatsApp o Email: il PDF viene allegato in automatico." : "Scegli WhatsApp o Email: il cliente riceve il PDF da scaricare con un tocco."}</DialogDescription>
        </DialogHeader>

        <Tabs value={channel} onValueChange={setChannel}>
          <TabsList className="grid grid-cols-2 w-full h-12">
            <TabsTrigger value="whatsapp" className="h-10 text-base" data-testid="send-tab-whatsapp"><MessageCircle className="w-4 h-4 mr-1.5 text-emerald-600" /> WhatsApp</TabsTrigger>
            <TabsTrigger value="email" className="h-10 text-base" data-testid="send-tab-email"><Mail className="w-4 h-4 mr-1.5 text-primary" /> Email</TabsTrigger>
          </TabsList>
          <TabsContent value="whatsapp" className="space-y-2 pt-2">
            <Label>Numero WhatsApp del cliente</Label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="es. 3714331327" className="h-11 text-base" data-testid="send-phone-input" />
            <p className="text-sm text-muted-foreground">Il prefisso +39 si aggiunge da solo.</p>
          </TabsContent>
          <TabsContent value="email" className="space-y-2 pt-2">
            <Label>Email del cliente</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="cliente@email.it" className="h-11 text-base" data-testid="send-email-input" />
          </TabsContent>
        </Tabs>

        <div className="space-y-2">
          <Label>Messaggio</Label>
          <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={6} className="text-base" data-testid="send-message-textarea" />
        </div>

        <div className="flex items-start gap-2 text-sm text-muted-foreground bg-secondary/60 rounded-lg p-3" data-testid="send-info">
          <Info className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{channel === "whatsapp"
            ? "Sul telefono si apre WhatsApp con il PDF già allegato: scegli la chat e invia. Sul computer il PDF viene scaricato e si apre WhatsApp Web: trascina il file nella chat."
            : mailOk ? "L'email parte dalla tua casella con il PDF allegato."
              : <>L'email arriva al cliente con un pulsante per scaricare il PDF.{user?.role === "admin" && <> Per allegarlo direttamente <Link to="/posta" className="text-primary underline" onClick={onClose}>collega la tua casella in Posta</Link>.</>}</>}</span>
        </div>

        {channel === "whatsapp" ? (
          <Button onClick={sendWhatsapp} disabled={busy} className="w-full h-12 text-base bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="send-whatsapp-pdf-button">
            {busy ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : waOk ? <Send className="w-5 h-5 mr-2" /> : <MessageCircle className="w-5 h-5 mr-2" />} Invia su WhatsApp
          </Button>
        ) : (
          <Button onClick={sendEmail} disabled={busy} className="w-full h-12 text-base" data-testid="send-email-pdf-button">
            {busy ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Mail className="w-5 h-5 mr-2" />} {mailOk ? "Invia email con PDF allegato" : "Invia email con il PDF"}
          </Button>
        )}

        <div aria-hidden style={{ position: "fixed", left: "-10000px", top: 0, width: 820, background: "#fff", zIndex: -1 }}>
          {docType === "ddt"
            ? <DdtPDF ddt={doc} settings={settings} domId="send-print-area" />
            : <QuotePDF quote={doc} settings={settings} contact={contact} tipo={D.pdf} domId="send-print-area" />}
        </div>
      </DialogContent>
    </Dialog>
  );
}
